import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Plus, Users, Copy, Trash2, Check, Clock, Mail, Gift } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

export default function InvitesPage() {
  const { token } = useAuth();
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState({ totalInvites: 0, acceptedInvites: 0, pendingInvites: 0 });
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [newInviteLink, setNewInviteLink] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invitesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/invites`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/dashboard/invites/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setInvites(invitesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading invites:', error);
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.post(
        `${API_URL}/api/dashboard/invites`,
        { email: inviteEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const inviteLink = `${APP_URL}/signup-provider?invite=${res.data.inviteCode}`;
      setNewInviteLink(inviteLink);
      toast.success('Invite created successfully!');
      loadData();
    } catch (error) {
      console.error('Error creating invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = (inviteCode) => {
    const link = `${APP_URL}/signup-provider?invite=${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard!');
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/dashboard/invites/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invite revoked');
      loadData();
    } catch (error) {
      console.error('Error deleting invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const closeDialog = () => {
    setShowInviteDialog(false);
    setInviteEmail('');
    setNewInviteLink('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Invite Providers</h2>
            <p className="text-muted-foreground">
              Invite other service providers to join RanchWorks
            </p>
          </div>
          <Button onClick={() => setShowInviteDialog(true)} data-testid="invite-btn">
            <Plus className="mr-2 h-4 w-4" />
            Invite a Provider
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalInvites}</p>
                  <p className="text-sm text-muted-foreground">Total Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.acceptedInvites}</p>
                  <p className="text-sm text-muted-foreground">Accepted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pendingInvites}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-semibold text-primary">Help RanchWorks Grow</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Know other farriers, trainers, or ranch service providers? Invite them to join RanchWorks! 
                  When they sign up using your invite link, they'll see a special welcome message from you.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invites List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : invites.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No Invites Yet</h3>
              <p className="text-muted-foreground mt-1 mb-4">
                Start inviting other service providers to join RanchWorks.
              </p>
              <Button onClick={() => setShowInviteDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Send Your First Invite
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Your Invites</CardTitle>
              <CardDescription>
                Track the status of providers you've invited
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {invites.map((invite) => (
                  <div 
                    key={invite.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg gap-4"
                    data-testid={`invite-${invite.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${invite.accepted ? 'bg-green-100' : 'bg-amber-100'}`}>
                        {invite.accepted ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Clock className="h-4 w-4 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{invite.email}</p>
                        <p className="text-sm text-muted-foreground">
                          Invited {format(new Date(invite.createdAt), 'MMM d, yyyy')}
                        </p>
                        {invite.accepted && invite.acceptedAt && (
                          <p className="text-sm text-green-600">
                            Joined {format(new Date(invite.acceptedAt), 'MMM d, yyyy')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={invite.accepted ? 'default' : 'secondary'}>
                        {invite.accepted ? 'Accepted' : 'Pending'}
                      </Badge>
                      {!invite.accepted && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyLink(invite.inviteCode)}
                            data-testid={`copy-link-${invite.id}`}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Link
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteInvite(invite.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-invite-${invite.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a Provider</DialogTitle>
              <DialogDescription>
                Send an invite to another service provider to join RanchWorks.
              </DialogDescription>
            </DialogHeader>
            
            {newInviteLink ? (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <p className="font-semibold text-green-800">Invite Created!</p>
                  </div>
                  <p className="text-sm text-green-700 mb-3">
                    Share this link with the provider you want to invite:
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={newInviteLink}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(newInviteLink);
                        toast.success('Link copied!');
                      }}
                      variant="outline"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={closeDialog}>Done</Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Provider's Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="provider@example.com"
                    data-testid="invite-email-input"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateInvite} disabled={submitting} data-testid="send-invite-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Create Invite
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
